// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { type CancellationProtection } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ButtonBase, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction";
import { List, Pill, Text } from "@src/appV2/redesign/system/ui-components";
import { useHistory } from "react-router-dom";

import { type ComputedArrivalData } from "../../ArrivalTime/computeArrivalData";
import {
  SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { useWorkplaceModalsDataContext } from "../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { WorkplaceNoteIdentifier } from "../../Workplace/constants";
import { useWorkplaceNotesParsed } from "../../Workplace/useWorkplaceNotesParsed";
import { WorkingThisShiftListItem } from "../../WorkWithFriends/components/WorkingThisShiftListItem";
import { type WorkerPublicProfile } from "../../WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { WorkWithFriendsProvider } from "../../WorkWithFriends/WorkWithFriendsContext";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import { type ShiftType } from "../constants";
import { ShiftDoublePill } from "../Double/Pill";
import { ShiftDurationListItem } from "../DurationListItem";
import { ShiftHighlightsAndMapListItem } from "../HighlightsAndMapListItem";
import { type TimeSlot, type Window } from "../Open/types";
import { type ShiftSlotCoalesced } from "../Open/useGetShiftsSlotV2";
import { ShiftAuctionInfoListItem } from "../ShiftAuction/ListItem";
import { ShiftTransferableInfo } from "../TransferableInfo";
import type {
  ModalShiftAuctionData,
  ModalShiftSpecificationData,
  ModalWorkplaceData,
  WindowRestriction,
} from "../types";
import { ShiftWorkplaceListItem } from "../WorkplaceListItem";
import { ShiftBottomSheetBookabilityCardsContainer } from "./BookabilityCardsContainer";
import { ShiftBottomSheetSpecification } from "./Specification";
import { useFooterData } from "./useFooterData";

interface ShiftBottomSheetSheetContentProps {
  shiftId: string;
  shiftType: ShiftType;
  shiftTimeSlot: TimeSlot;
  shiftStartIso: string;
  shiftEndIso: string;
  shiftDurationInHours: number;
  breakDurationInMinutes?: number;
  requiresBreak?: boolean;
  shiftQualification: string;
  workplace: ModalWorkplaceData;
  bookabilityStatus: BookabilityStatusItem;
  blockingRequirementsContext?: BlockingRequirementsContext;
  hasMarkedInterest?: boolean;
  shiftFailedLoading: boolean;
  hasHoursRestrictionConflict: boolean;
  shiftSlots?: ShiftSlotCoalesced;
  friendsMap?: Map<string, WorkerPublicProfile>;
  shiftSpecifications?: ModalShiftSpecificationData[];
  outboundFriendRequestsSet?: Set<string>;
  hideWorkplaceCallToActionIcon?: boolean;
  hideWorkingThisShiftListItem?: boolean;
  disableWorkplaceListItemButton?: boolean;
  restrictedToWindow?: WindowRestriction;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  showTransferableInfo?: boolean;
  bookabilityCardsShakeableRef?: React.RefObject<HTMLElement | undefined>;
  isIndependentContractorBadgesRequired?: boolean;
  cancellationProtection?: CancellationProtection;
  isCancellationProtectionPremiumApplied?: boolean;
  priorityTill?: Date;
  window?: Window;
  isShortLeadTime?: boolean;
  minutesElapsed?: number;
  shiftAuction?: ModalShiftAuctionData | undefined;
  computedArrivalData?: ComputedArrivalData;
  preShiftOrientationAdjustedStart?: Date;
  preShiftOrientationLengthInMinutes?: number;
  isSingleOrientationShift?: boolean;
}

export function ShiftBottomSheetContent(props: ShiftBottomSheetSheetContentProps) {
  const {
    shiftId,
    shiftTimeSlot,
    shiftStartIso,
    shiftEndIso,
    shiftDurationInHours,
    breakDurationInMinutes = 0,
    requiresBreak = false,
    shiftQualification,
    workplace,
    bookabilityStatus,
    shiftType,
    hasHoursRestrictionConflict,
    shiftFailedLoading,
    hasMarkedInterest,
    blockingRequirementsContext,
    shiftSlots,
    friendsMap,
    shiftSpecifications,
    outboundFriendRequestsSet,
    hideWorkplaceCallToActionIcon,
    hideWorkingThisShiftListItem,
    disableWorkplaceListItemButton,
    restrictedToWindow,
    scrollContainerRef,
    showTransferableInfo,
    bookabilityCardsShakeableRef,
    isIndependentContractorBadgesRequired,
    cancellationProtection,
    isCancellationProtectionPremiumApplied,
    priorityTill,
    window,
    isShortLeadTime,
    minutesElapsed,
    shiftAuction,
    computedArrivalData,
    preShiftOrientationAdjustedStart,
    preShiftOrientationLengthInMinutes,
    isSingleOrientationShift,
  } = props;

  const { name: workplaceName, location: workplaceLocation, fullAddress } = workplace.attributes;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();
  const { double, navigateToModal } = useShiftModalsDataContext();

  const { isBookable } = useFooterData();

  const { data: workplaceNotes } = useWorkplaceNotesParsed(workplace.id);
  const hasLateClockOutPolicy = isDefined(
    workplaceNotes?.[WorkplaceNoteIdentifier.LATE_CLOCK_OUT_POLICY]
  );

  return (
    <Box ref={scrollContainerRef} sx={{ overflowY: "auto" }}>
      {isDefined(shiftAuction) && (
        <List sx={{ px: 6 }}>
          <ShiftAuctionInfoListItem auctionEndTime={shiftAuction?.attributes.endTime} />
        </List>
      )}
      <Box ref={bookabilityCardsShakeableRef}>
        <ShiftBottomSheetBookabilityCardsContainer
          shiftId={shiftId}
          shiftType={shiftType}
          shiftStartIso={shiftStartIso}
          shiftQualification={shiftQualification}
          workplace={workplace}
          bookabilityStatus={bookabilityStatus}
          hasHoursRestrictionConflict={hasHoursRestrictionConflict}
          shiftFailedLoading={shiftFailedLoading}
          hasMarkedInterest={hasMarkedInterest}
          blockingRequirementsContext={blockingRequirementsContext}
          restrictedToWindow={restrictedToWindow}
          isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
          cancellationProtection={cancellationProtection}
          isCancellationProtectionPremiumApplied={isCancellationProtectionPremiumApplied}
          priorityTill={priorityTill}
          window={window}
          isShortLeadTime={isShortLeadTime}
          computedArrivalData={computedArrivalData}
          preShiftOrientationAdjustedStart={preShiftOrientationAdjustedStart}
          preShiftOrientationLengthInMinutes={preShiftOrientationLengthInMinutes}
          isSingleOrientationShift={isSingleOrientationShift}
        />
      </Box>

      <List sx={{ px: 6 }}>
        <ShiftDurationListItem
          divider
          shiftTimeSlot={shiftTimeSlot}
          shiftStartIso={shiftStartIso}
          shiftEndIso={shiftEndIso}
          shiftDurationInHours={shiftDurationInHours}
          breakDurationInMinutes={breakDurationInMinutes}
          requiresBreak={requiresBreak}
          workplaceTimeZone={workplaceLocation.timezone}
          minutesElapsed={minutesElapsed}
          highlights={
            hasLateClockOutPolicy
              ? [
                  <Pill
                    key="late-clock-out-policy"
                    size="medium"
                    label="Late clock-out rules"
                    iconType="calendar-clock"
                    sx={{ width: "max-content" }}
                  />,
                ]
              : []
          }
        />

        {isDefined(double) && (
          <>
            <ButtonBase
              sx={(theme) => ({
                backgroundColor: theme.palette.shift?.double?.footerBackground,
                borderRadius: 2,
                py: 3,
                px: 4,
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `1px solid ${theme.palette.divider}`,
              })}
              onClick={() => {
                navigateToModal(SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_PATH, {
                  pathParams: { shiftId },
                });
              }}
            >
              <ShiftDoublePill />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  View double →
                </Text>
              </Stack>
            </ButtonBase>
          </>
        )}

        {showTransferableInfo && <ShiftTransferableInfo />}

        {shiftSpecifications?.map((specification) => (
          <ShiftBottomSheetSpecification
            key={`${specification.category}-${specification.requirement}`}
            category={specification.category}
            requirement={specification.requirement}
          />
        ))}

        <ShiftWorkplaceListItem
          workplaceId={workplace.id}
          workplaceName={workplaceName}
          workplaceType={workplace.attributes.type ?? ""}
          workplaceLocation={workplaceLocation}
          hideCallToActionIcon={hideWorkplaceCallToActionIcon}
          onClick={() => {
            if (disableWorkplaceListItemButton) {
              return;
            }

            logEvent(APP_V2_USER_EVENTS.VIEWED_FACILITY_PROFILE, {
              workplaceId: workplace.id,
              trigger: "Shift Bottom Sheet Highlights Card",
            });

            history.push(
              generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
                workplaceId: workplace.id,
              })
            );
          }}
        />

        <ShiftHighlightsAndMapListItem
          workplaceId={workplace.id}
          workplaceName={workplaceName}
          workplaceLocation={workplaceLocation}
          workplaceFormattedAddress={fullAddress?.formatted}
        />

        {!hideWorkingThisShiftListItem && (
          <WorkWithFriendsProvider>
            <WorkingThisShiftListItem
              shiftSlots={shiftSlots}
              shiftId={shiftId}
              friendsMap={friendsMap}
              outboundFriendRequestsSet={outboundFriendRequestsSet}
              isBookable={isBookable}
            />
          </WorkWithFriendsProvider>
        )}
      </List>
    </Box>
  );
}
/* eslint-enable max-lines */
